import { Task, WeeklyPlan, PhysicalUnit, Responsible, ProjectProgressSnapshot, ProtocolState, ProtocolConfig, ProjectInfo, GanttBaseline } from '../types';

export interface SCurvePoint {
    name: string;
    date: string;
    weekIndex: number;
    plan: number;
    real: number | null;
}

/**
 * Calculates the S-Curve data (Planned vs Real progress over time)
 * @param tasks - List of project tasks
 * @returns Array of SCurvePoint for charting
 */
export const calculateSCurve = (tasks: Task[], progressHistory: ProjectProgressSnapshot[] = [], syncDay: number = 5): SCurvePoint[] => {
    const flatTasks = getLeafTasksOnly(tasks);

    if (flatTasks.length === 0) return [];

    // Find global range
    const starts = flatTasks.map(t => new Date(t.start! + "T12:00:00").getTime());
    const ends = flatTasks.map(t => new Date((t.end || t.start)! + "T12:00:00").getTime());
    const minStart = new Date(Math.min(...starts));
    const maxEnd = new Date(Math.max(...ends));

    // Normalize start date to syncDay (default Friday) on or BEFORE minStart
    let curr = new Date(minStart);
    while (curr.getDay() !== (syncDay % 7)) {
        curr.setDate(curr.getDate() - 1);
    }

    const totalProjectDuration = flatTasks.reduce((sum, t) => sum + (t.duration || 0), 0);
    const minStartTime = minStart.getTime();

    // Generate weekly points
    const points: SCurvePoint[] = [];

    while (curr <= maxEnd || points.length < 4) {
        const dateStr = curr.toISOString().split('T')[0];
        const dateLabel = curr.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
        const compareTime = curr.getTime();

        // 1. Calculate Planned Progress
        let plannedDurationAccum = 0;
        flatTasks.forEach(t => {
            const tStart = new Date(t.start! + "T12:00:00").getTime();
            const tEnd = new Date((t.end || t.start)! + "T12:00:00").getTime();
            const tDur = t.duration || 0;

            if (compareTime >= tEnd) {
                plannedDurationAccum += tDur;
            } else if (compareTime > tStart) {
                const totalDays = Math.max(86400000, tEnd - tStart);
                const elapsedDays = compareTime - tStart;
                plannedDurationAccum += (elapsedDays / totalDays) * tDur;
            }
        });

        let planPct = totalProjectDuration > 0
            ? Number(truncateProgress((plannedDurationAccum / totalProjectDuration) * 100, 2))
            : 0;

        // 2. Calculate Real Progress (from history)
        let realPct: number | null = null;

        if (progressHistory && Array.isArray(progressHistory) && progressHistory.length > 0) {
            const validHistory = progressHistory.filter(h => h && h.date && h.progress !== undefined && h.progress > 0);
            if (validHistory.length > 0) {
                const sortedHistory = [...validHistory].sort((a, b) => a.date.localeCompare(b.date));
                const lastRecord = sortedHistory[sortedHistory.length - 1];
                const lastRecordTime = new Date(lastRecord.date + "T23:59:59").getTime();

                if (compareTime <= lastRecordTime) {
                    // Filter records strictly up to dateStr (exact date matching)
                    const pastOrCurrentRecords = sortedHistory.filter(r => r.date <= dateStr);

                    if (pastOrCurrentRecords.length > 0) {
                        realPct = pastOrCurrentRecords[pastOrCurrentRecords.length - 1].progress;
                    } else {
                        realPct = 0;
                    }
                }
            }
        }

        // Force initial baseline point at start of project to 0% for BOTH planned and real progress
        if (points.length === 0) {
            planPct = 0;
            if (progressHistory && progressHistory.length > 0) {
                realPct = 0;
            }
        }

        points.push({
            name: dateLabel,
            date: dateStr,
            weekIndex: points.length + 1,
            plan: Math.min(100, planPct),
            real: realPct !== null ? Math.min(100, realPct) : null
        });

        curr.setDate(curr.getDate() + 7);
        if (points.length > 100) break; // Hard limit for safety
    }
    return points;
};

/**
 * Redondea un número a un máximo de 2 decimales (Regla de negocio: max 2 decimales).
 */
export const roundToTwoDecimals = (val: number): number => {
    if (isNaN(val) || val === null || val === undefined) return 0;
    return Math.round(Number(val) * 100) / 100;
};

/**
 * Formatea un número con máximo 2 decimales limpios.
 */
export const formatTwoDecimals = (val: number, decimals: number = 2): string => {
    if (isNaN(val) || val === null || val === undefined) return (0).toFixed(decimals);
    const rounded = Math.round(Number(val) * Math.pow(10, decimals)) / Math.pow(10, decimals);
    return rounded.toFixed(decimals);
};

/**
 * Trunca o redondea un número de avance a un máximo de 2 decimales.
 * Aplica redondeo al segundo decimal (Math.round) para evitar residuos flotantes como 99.80%.
 */
export const truncateProgress = (val: number, decimals: number = 2): string => {
    if (isNaN(val) || val === null || val === undefined) return (0).toFixed(decimals);
    const factor = Math.pow(10, decimals);
    return (Math.round(Number(val) * factor) / factor).toFixed(decimals);
};

/**
 * Resuelve el estado guardado de un protocolo buscando tanto por su ID correlativo (protocolId)
 * como por coincidencia de (taskId, unitId), eligiendo siempre la entrada más completa o reciente
 * cuando existen múltiples alias (ej. protocolos enviados desde móvil con llave basada en el nombre de la unidad).
 */
export const findSavedProtocolEntry = (
    savedProtocols: Record<string, ProtocolState> = {},
    protocolId?: string | null,
    taskId?: string | null,
    unitId?: string | null
): { key: string; state: ProtocolState; allKeys: string[] } | null => {
    if (!savedProtocols || typeof savedProtocols !== 'object') return null;

    const candidates: { key: string; state: ProtocolState }[] = [];
    const seenKeys = new Set<string>();

    if (protocolId && savedProtocols[protocolId]) {
        candidates.push({ key: protocolId, state: savedProtocols[protocolId] });
        seenKeys.add(protocolId);
    }

    const resolvedTaskId = taskId || (protocolId && savedProtocols[protocolId]?.taskId) || null;
    const resolvedUnitId = unitId || (protocolId && savedProtocols[protocolId]?.unitId) || null;

    if (resolvedTaskId && resolvedUnitId) {
        for (const [k, p] of Object.entries(savedProtocols)) {
            if (!p || seenKeys.has(k)) continue;
            if (p.taskId === resolvedTaskId && p.unitId === resolvedUnitId) {
                candidates.push({ key: k, state: p });
                seenKeys.add(k);
            }
        }
    }

    if (candidates.length === 0) return null;
    if (candidates.length === 1) {
        return { key: candidates[0].key, state: candidates[0].state, allKeys: [candidates[0].key] };
    }

    const scoreCandidate = (p: ProtocolState): number => {
        let score = 0;
        if (p.status === 'Cerrado') score += 100000;
        else if (p.status === 'Resuelto') score += 50000;
        else if (p.status === 'Rechazado') score += 40000;
        else if (p.status === 'Abierto') score += 10000;
        score += (p.currentReviewerIndex || 0) * 5000;
        score += Object.keys(p.signatures || {}).length * 1000;
        score += (p.history || []).length * 200;
        score += Object.values(p.checklist || {}).filter((it: any) => it && it.status && it.status !== 'pending').length * 50;
        return score;
    };

    candidates.sort((a, b) => {
        const scoreDiff = scoreCandidate(b.state) - scoreCandidate(a.state);
        if (scoreDiff !== 0) return scoreDiff;
        const tsA = a.state.updatedAt ? Date.parse(a.state.updatedAt) || 0 : 0;
        const tsB = b.state.updatedAt ? Date.parse(b.state.updatedAt) || 0 : 0;
        return tsB - tsA;
    });

    return {
        key: candidates[0].key,
        state: candidates[0].state,
        allKeys: candidates.map(c => c.key)
    };
};

export const findSavedProtocol = (
    savedProtocols: Record<string, ProtocolState> = {},
    protocolId?: string | null,
    taskId?: string | null,
    unitId?: string | null
): ProtocolState | undefined => {
    const entry = findSavedProtocolEntry(savedProtocols, protocolId, taskId, unitId);
    return entry ? entry.state : undefined;
};

/**
 * Verifica si un protocolo guardado tenía parámetros completados con una plantilla base distinta
 * a la que actualmente tiene configurada la partida (ej. se rellenó con "Alcantarillado" y luego
 * se cambió la plantilla de la partida a "Tabiquería Interior").
 */
export const isProtocolTemplateChangedWithCompletedParams = (
    savedState: ProtocolState | undefined | null,
    config: ProtocolConfig | undefined | null
): boolean => {
    if (!savedState || !config) return false;

    const checklistEntries = Object.values(savedState.checklist || {});
    const hasCompletedParams = checklistEntries.some((it: any) =>
        it && (
            (it.status && it.status !== 'pending') ||
            (typeof it.comment === 'string' && it.comment.trim() !== '') ||
            (Array.isArray(it.photos) && it.photos.length > 0)
        )
    );
    if (!hasCompletedParams) return false;

    const norm = (s?: string) => String(s || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const savedName = norm(savedState.protocolName);
    const configName = norm(config.protocolName);
    if (savedName && configName && savedName !== configName) {
        return true;
    }

    const savedPrefix = savedState.id && savedState.id.includes('-')
        ? norm(savedState.id.substring(0, savedState.id.lastIndexOf('-')))
        : '';
    const configCode = norm(config.code);
    if (savedPrefix && configCode && savedPrefix !== 'prt' && configCode !== 'prt' && savedPrefix !== configCode) {
        return true;
    }

    // Si los parámetros guardados no coinciden con ninguno de los IDs de ítems de la plantilla actual
    const configItems = config.items || [];
    if (configItems.length > 0 && checklistEntries.length > 0) {
        const configIdSet = new Set(configItems.map(i => i.id));
        const rawKeys = Object.keys(savedState.checklist || {});
        const anyMatch = rawKeys.some(k => configIdSet.has(k) || configIdSet.has((savedState.checklist as any)?.[k]?.itemId));
        if (!anyMatch) {
            return true;
        }
    }

    return false;
};

/**
 * Normaliza el diccionario de checklist de un protocolo guardado contra los ítems actuales de su configuración.
 * - Si la plantilla base cambió a un protocolo distinto (ej. Alcantarillado -> Tabiquería Interior),
 *   los parámetros vuelven a 0 (en blanco) para realizarse de nuevo.
 * - Si es la misma plantilla base (re-importada con nuevos IDs), reconcilia las respuestas por posición (0..N-1).
 */
export const normalizeProtocolChecklist = (
    rawChecklist: Record<string, any> | undefined | null,
    configItems: { id: string; text?: string }[] | undefined | null,
    savedState?: ProtocolState | null,
    config?: ProtocolConfig | null
): Record<string, { status: 'pass' | 'fail' | 'na' | 'pending'; comment: string; photos: string[] }> => {
    const result: Record<string, { status: 'pass' | 'fail' | 'na' | 'pending'; comment: string; photos: string[] }> = {};
    if (!rawChecklist || typeof rawChecklist !== 'object') return result;

    // Si cambió la plantilla base a otro protocolo distinto, los parámetros deben volver a 0 (en blanco)
    if (savedState && config && isProtocolTemplateChangedWithCompletedParams(savedState, config)) {
        return {};
    }

    const entries = Object.entries(rawChecklist)
        .map(([k, v]) => ({
            key: v?.itemId || k,
            status: (v?.status || 'pending') as 'pass' | 'fail' | 'na' | 'pending',
            comment: v?.comment || '',
            photos: Array.isArray(v?.photos) ? [...v.photos] : []
        }))
        .filter(e => e.key);

    if (entries.length === 0) return result;

    if (!configItems || configItems.length === 0) {
        entries.forEach(e => {
            result[e.key] = { status: e.status, comment: e.comment, photos: e.photos };
        });
        return result;
    }

    const configIdSet = new Set(configItems.map(it => it.id));
    const matchedEntries = entries.filter(e => configIdSet.has(e.key));
    const unmatchedEntries = entries.filter(e => !configIdSet.has(e.key));

    // 1. Asignar coincidencias exactas de itemId
    matchedEntries.forEach(e => {
        result[e.key] = { status: e.status, comment: e.comment, photos: e.photos };
    });

    // 2. Si existen respuestas guardadas cuyos IDs quedaron huérfanos por re-importación de plantilla,
    // mapearlas posicionalmente sobre los ítems de configuración vacíos.
    if (unmatchedEntries.length > 0) {
        const availableConfigItems = configItems.filter(it => !result[it.id]);
        unmatchedEntries.forEach((orphan, idx) => {
            const targetConfigItem = availableConfigItems[idx];
            if (targetConfigItem) {
                result[targetConfigItem.id] = {
                    status: orphan.status,
                    comment: orphan.comment,
                    photos: orphan.photos
                };
            } else if (configItems.length > 0) {
                // Si hubiera más ítems antiguos que actuales y tienen fotos/comentarios, preservarlos en el último ítem
                const lastId = configItems[configItems.length - 1].id;
                const lastItem = result[lastId] || { status: 'pending', comment: '', photos: [] };
                const mergedPhotos = Array.from(new Set([...(lastItem.photos || []), ...(orphan.photos || [])]));
                const mergedComment = [lastItem.comment, orphan.comment].filter(Boolean).join(' | ');
                result[lastId] = {
                    ...lastItem,
                    comment: mergedComment,
                    photos: mergedPhotos
                };
            }
        });
    }

    return result;
};

/**
 * Calcula el ID correlativo canónico de un protocolo (ej. 'LD_LV_TERM_TABINT-027')
 * siguiendo exactamente el mismo recorrido de tareas y unidades que Control de Calidad.
 */
export const computeCanonicalProtocolId = (
    targetTaskId: string,
    targetUnitId: string,
    tasks: Task[] = [],
    units: PhysicalUnit[] = [],
    protocolConfigs: Record<string, ProtocolConfig> = {},
    matrixData: Record<string, any> = {}
): string | null => {
    let counter = 1;
    let foundId: string | null = null;

    const traverse = (taskList: Task[]) => {
        for (const task of taskList) {
            if (foundId) return;
            const hasWeeklySubtasks = !!(task.children && task.children.some(c => c.isWeeklySubtask && !c.isDeleted));
            if (task.type === 'task' && !hasWeeklySubtasks) {
                const config = protocolConfigs?.[task.id];
                if (config && config.applies) {
                    for (const unit of units) {
                        const key = `quality_${task.id}_${unit.id}`;
                        if (isMatrixValueApplies(matrixData?.[key])) {
                            const codePrefix = config.code || 'PRT';
                            const formattedCounter = String(counter).padStart(3, '0');
                            const protocolId = `${codePrefix}-${formattedCounter}`;
                            if (task.id === targetTaskId && unit.id === targetUnitId) {
                                foundId = protocolId;
                                return;
                            }
                            counter++;
                        }
                    }
                }
            }
            if (task.children && task.children.length > 0) {
                traverse(task.children);
            }
        }
    };

    traverse(tasks || []);
    return foundId;
};

export const isProtocolProgressed = (
    taskId: string,
    unitId: string,
    savedProtocols: Record<string, ProtocolState> = {},
    protocolConfigs: any = {}
): boolean => {
    return Object.values(savedProtocols || {}).some((p: ProtocolState) => {
        if (!p) return false;
        if (p.taskId === taskId && p.unitId === unitId) {
            const config = protocolConfigs?.[taskId];
            // Si se cambió la plantilla base del protocolo después de haberse completado,
            // debe realizarse de nuevo y no cuenta como protocolo enviado/aprobado.
            if (isProtocolTemplateChangedWithCompletedParams(p, config)) {
                return false;
            }
            const group = p.groupId
                ? config?.reviewerGroups?.find((g: any) => g.id === p.groupId)
                : config?.reviewerGroups?.find((g: any) => g.unitIds?.includes(p.unitId));

            if (group) {
                return (group.reviewers || []).some((rev: any, idx: number) => {
                    if (!rev?.generatesProgress) return false;
                    // Debe tener firma del revisor que genera avance
                    const hasSignature = !!p.signatures?.[idx];
                    if (!hasSignature) return false;

                    // Debe haber sido ENVIADO al siguiente revisor o estar completamente cerrado/aprobado
                    // (no basta con tenerlo guardado en borrador)
                    const isClosed = p.status === 'Cerrado';
                    const hasAdvancedPastStage = (p.currentReviewerIndex ?? 0) > idx;
                    const hasSentActionInHistory = (p.history || []).some((h: any) => 
                        (h.action === 'approved' || h.action === 'closed' || h.action === 'resolved') &&
                        (!rev.name || (h.user && (h.user.includes(rev.name) || h.user.includes(`Revisor ${idx + 1}`))))
                    );

                    return isClosed || hasAdvancedPastStage || hasSentActionInHistory;
                });
            }
        }
        return false;
    });
};

export const calculateUnitProgress = (
    taskId: string,
    unitId: string,
    protocolConfigs: any,
    savedProtocols: Record<string, ProtocolState>,
    weeklyPlans: WeeklyPlan[],
    tasks: Task[] = []
): number => {
    const taskObj = findTaskById(taskId, tasks);
    const parentTask = findParentTask(taskId, tasks);
    const isSub = taskObj?.isWeeklySubtask ?? false;
    const parentTaskId = (isSub && parentTask) ? parentTask.id : taskId;
    const subtaskId = taskId;

    const config = protocolConfigs[parentTaskId];
    const appliesProtocol = config?.applies ?? false;

    if (appliesProtocol && isProtocolProgressed(parentTaskId, unitId, savedProtocols, protocolConfigs)) {
        return 100;
    }

    // Sum up % Cumplimiento in all closed weekly plans
    let totalCumplimiento = 0;
    weeklyPlans.forEach(plan => {
        if (plan.status === 'Cerrada') {
            plan.allocations.forEach(alloc => {
                if ((alloc.taskId === subtaskId || alloc.taskId === parentTaskId) && alloc.unitId === unitId) {
                    totalCumplimiento += getAllocationCompliancePct(alloc);
                }
            });
        }
    });

    return Math.min(totalCumplimiento, 100);
};

/**
 * Obtiene el % de cumplimiento efectivo de una asignación semanal.
 * Si está ejecutada/cumplida, siempre es 100% (o el valor guardado si es > 0).
 * Previene el error donde percentCumplimiento: 0 sobreescribía executed: true debido al operador ??.
 */
export const getAllocationCompliancePct = (alloc: { executed?: boolean | null; percentCumplimiento?: number } | null | undefined): number => {
    if (!alloc) return 0;
    if (alloc.executed === true) {
        return (alloc.percentCumplimiento !== undefined && alloc.percentCumplimiento > 0) ? alloc.percentCumplimiento : 100;
    }
    return alloc.percentCumplimiento || 0;
};


/**
 * REGLA DE ORO DE MATRIZ EDT / UNIDADES FÍSICAS:
 * SIEMPRE que en una intersección Tarea/Unidad Física haya un número distinto de 0 (o != 0),
 * esto se leerá como "Aplica" y la unidad física será programable.
 * 
 * Retorna true si el valor representa una asignación válida (número != 0, '1', '0.5', 'Aplica', etc.),
 * y false si el valor es 0, '0', '0.0', '0.00', '-', '', null, undefined, false o 'no'.
 */
export const isMatrixValueApplies = (val: any): boolean => {
    if (val === undefined || val === null) return false;
    if (typeof val === 'boolean') return val;
    if (typeof val === 'number') return !isNaN(val) && val !== 0;

    const strVal = String(val).trim();
    if (strVal === '' || strVal === '-' || strVal.toLowerCase() === 'false' || strVal.toLowerCase() === 'no') return false;

    const num = parseFloat(strVal.replace(',', '.'));
    if (!isNaN(num)) {
        return num !== 0;
    }
    
    return strVal !== '0';
};

/**
 * REGLA DE ORO DE MATRIZ EDT / UNIDADES FÍSICAS:
 * Busca si existe un valor distinto de 0 en la matriz para una tarea y unidad física.
 * Revisa el ID de la tarea directamente, su tarea madre (si es subtarea semanal),
 * o cualquiera de sus subtareas hijas (si es una partida con desgloses).
 */
export const getTaskMatrixValue = (
    taskId: string,
    unitId: string,
    matrixData: Record<string, any>,
    tasks: Task[] = []
): any => {
    if (!matrixData) return undefined;
    const directVal = matrixData[`planning_${taskId}_${unitId}`];
    if (isMatrixValueApplies(directVal)) return directVal;

    const taskObj = findTaskById(taskId, tasks);

    if (taskObj?.isWeeklySubtask || (taskObj && findParentTask(taskId, tasks))) {
        const parentTask = findParentTask(taskId, tasks);
        if (parentTask) {
            const parentVal = matrixData[`planning_${parentTask.id}_${unitId}`];
            if (isMatrixValueApplies(parentVal)) return parentVal;
        }
    }

    if (taskObj && taskObj.children && taskObj.children.length > 0) {
        for (const child of taskObj.children) {
            const childVal = matrixData[`planning_${child.id}_${unitId}`];
            if (isMatrixValueApplies(childVal)) return childVal;
        }
    }

    return directVal;
};

export const isTaskUnitAssigned = (
    taskId: string,
    unitId: string,
    matrixData: Record<string, any>,
    tasks: Task[] = []
): boolean => {
    const val = getTaskMatrixValue(taskId, unitId, matrixData, tasks);
    return isMatrixValueApplies(val);
};

/**
 * REGLA ABSOLUTA DE CUMPLIMIENTO ACUMULADO Y SALDO COMPROMETIDO EN PROGRAMACIÓN SEMANAL:
 * Calcula el avance acumulado real (% CUMPLIMIENTO ACUM.) para una tarea y unidad física.
 * Considera:
 * 1. La suma del % cumplimiento de ejecuciones semanales cerradas.
 * 2. El avance por reconciliación de unidades físicas (reconciliationProgressUnits).
 * 3. El avance físico acumulado de la tarea (task.progress, task.baseProgress o calculateTaskProgressLocal).
 * 4. El estado del protocolo de calidad si aplica.
 */
export const getTaskUnitAccumulatedCompliance = (
    taskId: string,
    unitId: string,
    tasks: Task[] = [],
    weeklyPlans: WeeklyPlan[] = [],
    protocolConfigs: Record<string, ProtocolConfig> = {},
    savedProtocols: Record<string, ProtocolState> = {},
    overrideTaskProgress?: number,
    excludePlanId?: number | null
): number => {
    const taskObj = findTaskById(taskId, tasks);
    const parentTask = findParentTask(taskId, tasks);
    const targetTaskId = (taskObj?.isWeeklySubtask && parentTask) ? parentTask.id : taskId;

    // 1. Physical progress brought from Gantt / import / base progress / local progress calculation
    let ganttProgress = 0;
    if (overrideTaskProgress !== undefined && !isNaN(overrideTaskProgress)) {
        ganttProgress = overrideTaskProgress;
    } else {
        if (taskObj) {
            ganttProgress = Math.max(ganttProgress, taskObj.progress ?? taskObj.baseProgress ?? 0);
        }
        if (parentTask) {
            ganttProgress = Math.max(ganttProgress, parentTask.progress ?? parentTask.baseProgress ?? 0);
        }
    }

    // 2. Unit physical progress from reconciliation if defined
    let reconcileUnitVal = 0;
    if (taskObj?.reconciliationProgressUnits?.[unitId] !== undefined) {
        reconcileUnitVal = taskObj.reconciliationProgressUnits[unitId];
    } else if (parentTask?.reconciliationProgressUnits?.[unitId] !== undefined) {
        reconcileUnitVal = parentTask.reconciliationProgressUnits[unitId];
    }

    // 3. Cumulative execution recorded across closed weekly plans for this unit
    let sumWeeklyCumplimiento = 0;
    const safeWeeklyPlans = Array.isArray(weeklyPlans) ? weeklyPlans : [];
    safeWeeklyPlans
        .filter(p => p && p.status === 'Cerrada' && (excludePlanId === undefined || excludePlanId === null || p.id !== excludePlanId))
        .flatMap(p => Array.isArray(p.allocations) ? p.allocations : [])
        .forEach(a => {
            if (a && (a.taskId === taskId || a.taskId === targetTaskId) && a.unitId === unitId) {
                const pct = getAllocationCompliancePct(a);
                sumWeeklyCumplimiento += pct;
            }
        });

    // 4. Quality protocol check
    const safeConfigs = (protocolConfigs && typeof protocolConfigs === 'object') ? protocolConfigs : {};
    const safeSaved = (savedProtocols && typeof savedProtocols === 'object') ? savedProtocols : {};
    const appliesProtocol = (safeConfigs[targetTaskId]?.applies ?? safeConfigs[taskId]?.applies) ?? false;
    if (appliesProtocol && isProtocolProgressed(targetTaskId, unitId, safeSaved, safeConfigs)) {
        return 100;
    }

    let accumulated = Math.max(ganttProgress, reconcileUnitVal, sumWeeklyCumplimiento);
    return Math.min(100, Math.max(0, Number(accumulated.toFixed(2))));
};

export const calculateTaskProgress = (
    taskId: string,
    units: PhysicalUnit[],
    matrixData: Record<string, any>,
    protocolConfigs: any,
    projectInfo: ProjectInfo,
    savedProtocols: Record<string, ProtocolState>,
    weeklyPlans: WeeklyPlan[],
    tasks: Task[] = []
): number => {
    // 1. Get Total Assigned Units from EDT (Matrix)
    const assignedUnits = units.filter(u => isTaskUnitAssigned(taskId, u.id, matrixData, tasks));

    if (assignedUnits.length === 0) return 0;

    // 2. Sum progress of all assigned units using the cubicación weights
    let totalCubicacion = 0;
    let totalEjecutado = 0;

    assignedUnits.forEach(u => {
        const val = getTaskMatrixValue(taskId, u.id, matrixData, tasks);
        const cubicacion = (val && !isNaN(parseFloat(val))) ? parseFloat(val) : 1;
        
        // Pass tasks here so calculateUnitProgress can find targetTaskId and taskId correctly
        const progressPercent = calculateUnitProgress(taskId, u.id, protocolConfigs, savedProtocols, weeklyPlans, tasks);
        
        totalCubicacion += cubicacion;
        totalEjecutado += cubicacion * (progressPercent / 100);
    });

    if (totalCubicacion === 0) return 0;
    return (totalEjecutado / totalCubicacion) * 100;
};

/**
 * Helper to collect all terminal/leaf tasks (excluding summary tasks/folders and weekly planning subtasks)
 */
export const getLeafTasksOnly = (tasks: Task[]): Task[] => {
    const leaves: Task[] = [];
    const traverse = (list: Task[]) => {
        list.forEach(t => {
            if (t.isWeeklySubtask) return;
            const hasMasterChildren = t.children && t.children.some(c => !c.isWeeklySubtask);
            if (!hasMasterChildren && t.type !== 'folder') {
                leaves.push(t);
            } else if (t.children && hasMasterChildren) {
                traverse(t.children);
            }
        });
    };
    traverse(tasks);
    return leaves;
};

/**
 * Calculates the total project progress based on individual task completion.
 * Strict Rule: Evaluates ONLY terminal leaf tasks (tareas madre/hojas), ignoring summary folders.
 */
export const calculateProjectProgress = (
    tasks: Task[],
    units: PhysicalUnit[],
    matrixData: Record<string, any>,
    protocolConfigs: any,
    projectInfo: ProjectInfo,
    savedProtocols: Record<string, ProtocolState>,
    weeklyPlans: WeeklyPlan[]
): number => {
    if (!tasks || tasks.length === 0) return 0;

    const leafTasks = getLeafTasksOnly(tasks);
    if (leafTasks.length === 0) return 0;

    let sumaDuracionTotal = 0;
    let sumaDuracionEjecutada = 0;

    leafTasks.forEach(task => {
        let leafProgress = 0;
        if (task.children && task.children.length > 0) {
            const weeklySubtasks = task.children.filter(c => c.isWeeklySubtask);
            if (weeklySubtasks.length > 0) {
                const subtaskProgresses = weeklySubtasks.map(child => {
                    let physicalProgress = calculateTaskProgress(child.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                    const subtaskProgress = Math.min(100, (child.baseProgress !== undefined ? child.baseProgress : (child.progress || 0)) + physicalProgress);
                    const weight = child.weight !== undefined ? child.weight : (100 / weeklySubtasks.length);
                    return subtaskProgress * (weight / 100);
                });
                leafProgress = Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
            } else {
                const physicalProgress = calculateTaskProgress(task.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                leafProgress = Math.min(100, (task.baseProgress !== undefined ? task.baseProgress : (task.progress || 0)) + physicalProgress);
            }
        } else {
            const physicalProgress = calculateTaskProgress(task.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
            leafProgress = Math.min(100, (task.baseProgress !== undefined ? task.baseProgress : (task.progress || 0)) + physicalProgress);
        }

        const duration = task.duration || 0;
        if (duration > 0) {
            sumaDuracionTotal += duration;
            sumaDuracionEjecutada += duration * (leafProgress / 100);
        }
    });

    if (sumaDuracionTotal > 0) {
        return (sumaDuracionEjecutada / sumaDuracionTotal) * 100;
    } else {
        const sumProgress = leafTasks.reduce((acc, task) => {
            let leafProgress = 0;
            if (task.children && task.children.length > 0) {
                const weeklySubtasks = task.children.filter(c => c.isWeeklySubtask);
                if (weeklySubtasks.length > 0) {
                    const subtaskProgresses = weeklySubtasks.map(child => {
                        let physicalProgress = calculateTaskProgress(child.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                        const subtaskProgress = Math.min(100, (child.baseProgress !== undefined ? child.baseProgress : (child.progress || 0)) + physicalProgress);
                        const weight = child.weight !== undefined ? child.weight : (100 / weeklySubtasks.length);
                        return subtaskProgress * (weight / 100);
                    });
                    leafProgress = Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
                } else {
                    const physicalProgress = calculateTaskProgress(task.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                    leafProgress = Math.min(100, (task.baseProgress !== undefined ? task.baseProgress : (task.progress || 0)) + physicalProgress);
                }
            } else {
                const physicalProgress = calculateTaskProgress(task.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                leafProgress = Math.min(100, (task.baseProgress !== undefined ? task.baseProgress : (task.progress || 0)) + physicalProgress);
            }
            return acc + leafProgress;
        }, 0);
        return leafTasks.length > 0 ? (sumProgress / leafTasks.length) : 0;
    }
};

/**
 * Calculates a duration-weighted global progress percentage based on task.progress property.
 * Strict Rule: Evaluates ONLY terminal leaf tasks (tareas madre/hojas), ignoring summary folders.
 */
export const calculateWeightedProgress = (tasks: Task[]): number => {
    if (!tasks || tasks.length === 0) return 0;

    const leafTasks = getLeafTasksOnly(tasks);
    if (leafTasks.length === 0) return 0;

    let sumaDuracionTotal = 0;
    let sumaDuracionEjecutada = 0;

    leafTasks.forEach(task => {
        let leafProgress = task.progress || 0;
        if (task.children && task.children.length > 0) {
            const weeklySubtasks = task.children.filter(c => c.isWeeklySubtask);
            if (weeklySubtasks.length > 0) {
                const subtaskProgresses = weeklySubtasks.map(child => {
                    const weight = child.weight !== undefined ? child.weight : (100 / weeklySubtasks.length);
                    return (child.progress || 0) * (weight / 100);
                });
                leafProgress = Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
            }
        }

        const duration = task.duration || 0;
        if (duration > 0) {
            sumaDuracionTotal += duration;
            sumaDuracionEjecutada += duration * (leafProgress / 100);
        }
    });

    if (sumaDuracionTotal > 0) {
        return (sumaDuracionEjecutada / sumaDuracionTotal) * 100;
    } else {
        const sum = leafTasks.reduce((acc, t) => {
            let prog = t.progress || 0;
            if (t.children && t.children.length > 0) {
                const weeklySubtasks = t.children.filter(c => c.isWeeklySubtask);
                if (weeklySubtasks.length > 0) {
                    const subtaskProgresses = weeklySubtasks.map(child => {
                        const weight = child.weight !== undefined ? child.weight : (100 / weeklySubtasks.length);
                        return (child.progress || 0) * (weight / 100);
                    });
                    prog = Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
                }
            }
            return acc + prog;
        }, 0);
        return leafTasks.length > 0 ? (sum / leafTasks.length) : 0;
    }
};

/**
 * Calculates S-Curve delay days using the official project formula:
 * Días Atraso (Curva S) = Duración Total del Proyecto (Días) * (% Avance Programado - % Avance Real)
 */
export const calculateDelayDays = (
    curveData: any[],
    currentRealPct: number,
    currentDate: string,
    totalProjectDurationDays?: number,
    currentProgrammedPct?: number
): number => {
    if (curveData.length === 0) return 0;

    const sortedCurve = [...curveData].sort((a, b) => a.date.localeCompare(b.date));

    // Determine Total Project Duration in Days
    let totalDays = totalProjectDurationDays || 0;
    if (totalDays <= 0 && sortedCurve.length >= 2) {
        const startT = new Date(sortedCurve[0].date + "T12:00:00").getTime();
        const endT = new Date(sortedCurve[sortedCurve.length - 1].date + "T12:00:00").getTime();
        const diffMs = Math.abs(endT - startT);
        totalDays = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1);
    }
    if (totalDays <= 0) totalDays = 90;

    // Find Programmed Progress at currentDate
    let planPct = currentProgrammedPct;
    if (planPct === undefined || planPct === null) {
        const pointAtDate = sortedCurve.find(p => p.date === currentDate);
        if (pointAtDate) {
            planPct = pointAtDate.planActive !== undefined && pointAtDate.planActive !== null ? pointAtDate.planActive : (pointAtDate.plan || 0);
        } else {
            const closest = sortedCurve.filter(p => p.date <= currentDate);
            if (closest.length > 0) {
                const last = closest[closest.length - 1];
                planPct = last.planActive !== undefined && last.planActive !== null ? last.planActive : (last.plan || 0);
            } else {
                planPct = 0;
            }
        }
    }

    const diffPct = (planPct - currentRealPct) / 100;
    if (diffPct <= 0) return 0;

    const delayDaysEquivalent = Math.round(totalDays * diffPct);
    return Math.max(0, delayDaysEquivalent);
};

/**
 * Flattens a task tree into a flat array of tasks.
 */
export const flattenTasks = (list: Task[]): Task[] => {
    if (!list) return [];
    let flat: Task[] = [];
    list.forEach(t => {
        if (t.isDeleted) return;
        flat.push(t);
        if (t.children) {
            flat = flat.concat(flattenTasks(t.children.filter(c => !c.isDeleted)));
        }
    });
    return flat;
};

/**
 * Finds a task by ID within a tree structure.
 */
export const findTaskById = (id: string | null, list: Task[]): Task | null => {
    if (!id || !list) return null;
    for (const t of list) {
        if (t.id === id) return t;
        if (t.children) {
            const found = findTaskById(id, t.children);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Cleans leading bracketed codes like "[1.3.2.4.7] 3.2.5 Moldaje" -> "3.2.5 Moldaje".
 */
export const cleanTaskName = (name: string): string => {
    if (!name) return '';
    return name.replace(/^\[.*?\]\s*/, '');
};

/**
 * Finds the name of a task by ID within a tree structure.
 */
export const findTaskName = (id: string, list: Task[]): string => {
    if (!id || !list) return "";
    for (const t of list) {
        if (t.id === id) return cleanTaskName(t.name);
        if (t.children) {
            const found = findTaskName(id, t.children);
            if (found) return found;
        }
    }
    return "";
};

/**
 * Calculates the Critical Path Method (CPM) for a given set of tasks.
 * Marks `isCritical: true` on tasks that belong to the critical path (slack <= 0).
 */
/**
 * Calculates the Critical Path Method (CPM) for a given set of tasks.
 * Marks `isCritical: true` on tasks that belong to the critical path (slack <= 0).
 */
export const calculateCriticalPath = (
    tasks: Task[], 
    workingDays: boolean[] = [false, true, true, true, true, true, false], 
    holidays: { date: string; name: string }[] = []
): Task[] => {
    if (tasks.length === 0) return tasks;

    // Helper to get local date string
    const getLocalDateStr = (date: Date): string => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    // Helper to check if a day is a working day
    const isWorkingDay = (date: Date): boolean => {
        const dayOfWeek = date.getDay();
        if (!workingDays[dayOfWeek]) return false;
        const dateStr = getLocalDateStr(date);
        return !holidays.some(h => h.date === dateStr);
    };

    // Helper to calculate duration in working days
    const calculateWorkingDaysDuration = (start: string, end: string): number => {
        const startD = new Date(start + "T12:00:00");
        const endD = new Date(end + "T12:00:00");
        if (isNaN(startD.getTime()) || isNaN(endD.getTime())) return 0;
        if (startD > endD) return 0;

        let count = 0;
        let curr = new Date(startD);
        while (curr <= endD) {
            if (isWorkingDay(curr)) {
                count++;
            }
            curr.setDate(curr.getDate() + 1);
        }
        return count;
    };

    // Recursive function to compute folder dates
    const computeFolderDatesRecursive = (list: Task[]): Task[] => {
        return list.map(t => {
            if (t.type === 'folder') {
                const children = t.children ? computeFolderDatesRecursive(t.children) : [];
                
                let minStart: Date | null = null;
                let maxEnd: Date | null = null;

                // Filtrar para excluir subtareas semanales del cálculo de fechas de la carpeta
                const masterChildren = children.filter(c => !c.isWeeklySubtask);

                masterChildren.forEach(c => {
                    if (c.start) {
                        const d = new Date(c.start + "T12:00:00");
                        if (!isNaN(d.getTime())) {
                            if (!minStart || d < minStart) minStart = d;
                        }
                    }
                    if (c.end) {
                        const d = new Date(c.end + "T12:00:00");
                        if (!isNaN(d.getTime())) {
                            if (!maxEnd || d > maxEnd) maxEnd = d;
                        }
                    }
                });

                let start = t.start;
                let end = t.end;
                let duration = t.duration;

                if (minStart && maxEnd) {
                    start = getLocalDateStr(minStart);
                    end = getLocalDateStr(maxEnd);
                    duration = calculateWorkingDaysDuration(start, end);
                } else {
                    start = undefined;
                    end = undefined;
                    duration = 0;
                }

                return {
                    ...t,
                    children, // Se mantienen todos los hijos en el árbol
                    start,
                    end,
                    duration
                };
            } else {
                return t;
            }
        });
    };

    const tasksWithCalculatedFolders = computeFolderDatesRecursive(tasks);

    // 1. Flatten all tasks
    const flatTasks = flattenTasks(tasksWithCalculatedFolders);
    if (flatTasks.length === 0) return tasksWithCalculatedFolders;

    // Filter to only consider leaf tasks (tasks without children, ignoring weekly planning subtasks)
    const leafTasks = flatTasks.filter(t => 
        !t.isWeeklySubtask && 
        t.type === 'task' && 
        (!t.children || t.children.length === 0 || t.children.every(c => c.isWeeklySubtask))
    );
    if (leafTasks.length === 0) return tasksWithCalculatedFolders;

    // Find min and max dates to generate the working calendar range
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    leafTasks.forEach(t => {
        if (t.start) {
            const d = new Date(t.start + "T12:00:00");
            if (!minDate || d < minDate) minDate = d;
        }
        const tEnd = t.end || t.start;
        if (tEnd) {
            const d = new Date(tEnd + "T12:00:00");
            if (!maxDate || d > maxDate) maxDate = d;
        }
    });

    if (!minDate || !maxDate) return tasksWithCalculatedFolders;

    // Generate a range of calendar days with safety buffer (+/- 60 days)
    const startRange = new Date(minDate);
    startRange.setDate(startRange.getDate() - 60);
    const endRange = new Date(maxDate);
    endRange.setDate(endRange.getDate() + 60);

    const workingDaysList: string[] = [];
    const dateToIdx = new Map<string, number>();
    const idxToDate = new Map<number, string>();

    let curr = new Date(startRange);
    let idx = 0;
    while (curr <= endRange) {
        if (isWorkingDay(curr)) {
            const dateStr = getLocalDateStr(curr);
            workingDaysList.push(dateStr);
            dateToIdx.set(dateStr, idx);
            idxToDate.set(idx, dateStr);
            idx++;
        }
        curr.setDate(curr.getDate() + 1);
    }

    // Helper to find the closest working day index for a given date
    const getWorkingDayIdx = (dateStr: string): number => {
        if (dateToIdx.has(dateStr)) return dateToIdx.get(dateStr)!;
        // Fallback: search for the closest indexed working day
        const targetDate = new Date(dateStr + "T12:00:00");
        let bestIdx = 0;
        let minDiff = Number.MAX_SAFE_INTEGER;
        
        workingDaysList.forEach((wDateStr, wIdx) => {
            const wDate = new Date(wDateStr + "T12:00:00");
            const diff = Math.abs(wDate.getTime() - targetDate.getTime());
            if (diff < minDiff) {
                minDiff = diff;
                bestIdx = wIdx;
            }
        });
        return bestIdx;
    };

    // Data structures for CPM
    const es = new Map<string, number>(); // Early Start (index)
    const ef = new Map<string, number>(); // Early Finish (index)
    const ls = new Map<string, number>(); // Late Start (index)
    const lf = new Map<string, number>(); // Late Finish (index)

    // Forward pass equivalent: In this context, the user/Gantt already provides explicit start and end dates.
    // We treat the explicitly set start and end dates as Early Start (ES) and Early Finish (EF).
    leafTasks.forEach(t => {
        const tStart = t.start;
        const tEnd = t.end || t.start;
        if (tStart && tEnd) {
            es.set(t.id, getWorkingDayIdx(tStart));
            ef.set(t.id, getWorkingDayIdx(tEnd));
        }
    });

    // Build successors map for the Backward Pass
    const originalIdToTaskIdMap = new Map<string, string>();
    flatTasks.forEach(t => {
        if (t.id_original_project !== undefined && t.id_original_project !== null && !t.isWeeklySubtask) {
            originalIdToTaskIdMap.set(String(t.id_original_project), t.id);
        }
    });

    const successors = new Map<string, { id: string, type: string, lag: number }[]>();
    leafTasks.forEach(t => {
        if (t.predecessors) {
            t.predecessors.forEach(predStr => {
                const parsed = parseDependencyLink(predStr);
                if (parsed) {
                    const predOriginalId = String(parsed.rawId);
                    const predId = originalIdToTaskIdMap.get(predOriginalId);
                    if (predId) {
                        if (!successors.has(predId)) successors.set(predId, []);
                        successors.get(predId)!.push({ id: t.id, type: parsed.type, lag: parsed.lag });
                    }
                }
            });
        }
    });

    // Find the max end date (project's maximum Early Finish index)
    let maxEF = 0;
    ef.forEach((val) => {
        if (val > maxEF) maxEF = val;
    });

    // Backward pass: Initialize LF and LS for all leaf tasks to maxEF as a default starting ceiling
    leafTasks.forEach(t => {
        if (t.id && ef.has(t.id) && es.has(t.id)) {
            const taskEF = ef.get(t.id)!;
            const taskES = es.get(t.id)!;
            const duration = taskEF - taskES + 1;
            
            lf.set(t.id, maxEF);
            ls.set(t.id, maxEF - duration + 1);
        }
    });

    // Backward pass: Iterative relaxation for networks (handles dependency paths accurately)
    let changed = true;
    let iterations = 0;
    while(changed && iterations < 100) {
        changed = false;
        iterations++;
        leafTasks.forEach(t => {
            if (t.id && ef.has(t.id) && es.has(t.id)) {
                const succs = successors.get(t.id) || [];
                let minLF = maxEF; // Late Finish cannot exceed project end
                const taskEF = ef.get(t.id)!;
                const taskES = es.get(t.id)!;
                const duration = taskEF - taskES + 1;
                
                if (succs.length > 0) {
                    let calculatedMinLF = Number.MAX_SAFE_INTEGER;
                    succs.forEach(succ => {
                        const succLS = ls.get(succ.id);
                        const succLF = lf.get(succ.id);
                        const lag = succ.lag || 0;
                        
                        if (succLS !== undefined && succLF !== undefined) {
                            let possibleLF = Number.MAX_SAFE_INTEGER;
                            if (succ.type === "FS") {
                                possibleLF = succLS - 1 - lag; 
                            } else if (succ.type === "SS") {
                                possibleLF = (succLS - lag) + duration - 1; 
                            } else if (succ.type === "FF") {
                                possibleLF = succLF - lag; 
                            } else if (succ.type === "SF") {
                                possibleLF = (succLF - lag) + duration - 1; 
                            }
                            if (possibleLF < calculatedMinLF) calculatedMinLF = possibleLF;
                        }
                    });
                    if (calculatedMinLF !== Number.MAX_SAFE_INTEGER) {
                        minLF = Math.min(minLF, calculatedMinLF);
                    }
                }
                
                const currentLF = lf.get(t.id);
                if (currentLF === undefined || minLF < currentLF) {
                    lf.set(t.id, minLF);
                    ls.set(t.id, minLF - duration + 1);
                    changed = true;
                }
            }
        });
    }

    // Determine Critical tasks based on Slack = LF - EF <= 0 (working day difference)
    const criticalTaskIds = new Set<string>();
    leafTasks.forEach(t => {
         if (t.id) {
             const tEF = ef.get(t.id);
             const tLF = lf.get(t.id);
             if (tEF !== undefined && tLF !== undefined) {
                 const slack = tLF - tEF;
                 if (slack <= 0) {
                     criticalTaskIds.add(t.id);
                 }
             }
         }
    });

    // Reconstruct tree and mark folders if any child is critical
    const applyCriticalRecursive = (list: Task[]): { tasks: Task[], hasCriticalChild: boolean } => {
        let anyChildCritical = false;
        const mappedList = list.map(t => {
            const hasOnlyWeeklySubtasks = t.children && t.children.length > 0 && t.children.every(c => c.isWeeklySubtask);
            
            if (t.type === 'task' && (!t.children || t.children.length === 0 || hasOnlyWeeklySubtasks)) {
                const isCrit = criticalTaskIds.has(t.id);
                if (isCrit) anyChildCritical = true;
                const childrenMapped = t.children ? t.children.map(c => ({ ...c, isCritical: false })) : [];
                return { ...t, isCritical: isCrit, children: childrenMapped };
            } else {
                const childRes = applyCriticalRecursive(t.children || []);
                if (childRes.hasCriticalChild) anyChildCritical = true;
                return { ...t, children: childRes.tasks, isCritical: childRes.hasCriticalChild };
            }
        });
        return { tasks: mappedList, hasCriticalChild: anyChildCritical };
    };

    return applyCriticalRecursive(tasksWithCalculatedFolders).tasks;
};

/**
 * Finds the parent task of a given task ID.
 */
export const findParentTask = (id: string | null, list: Task[]): Task | null => {
    if (!id || !list) return null;
    for (const t of list) {
        if (t.children && t.children.some(c => c.id === id)) {
            return t;
        }
        if (t.children) {
            const found = findParentTask(id, t.children);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Obtiene el número correlativo real de la fila en la carta Gantt de escritorio (#65).
 * Resuelve id_original_project de la tarea, de su tarea madre si es desglose semanal,
 * o del índice secuencial en la lista plana maestra.
 * Retorna null si no tiene número (para ocultar el chip según regla de negocio).
 */
export const getTaskGanttRowNumber = (
    task: Task | null | undefined,
    tasksList: Task[]
): string | number | null => {
    if (!task) return null;

    // 1. Si la tarea ya tiene id_original_project explícito válido
    if (
        task.id_original_project !== undefined &&
        task.id_original_project !== null &&
        String(task.id_original_project).trim() !== '' &&
        String(task.id_original_project).trim() !== '-'
    ) {
        return task.id_original_project;
    }

    // 2. Si es una subtarea semanal (desglose), resolver correlativo desde la tarea madre
    if (task.isWeeklySubtask) {
        const parent = findParentTask(task.id, tasksList);
        if (parent) {
            const parentNumber = parent.id_original_project !== undefined &&
                parent.id_original_project !== null &&
                String(parent.id_original_project).trim() !== '' &&
                String(parent.id_original_project).trim() !== '-'
                ? parent.id_original_project
                : null;
            if (parentNumber) {
                const subtasks = (parent.children || []).filter(c => c.isWeeklySubtask && !c.isDeleted);
                const subIdx = subtasks.findIndex(c => c.id === task.id);
                return subIdx >= 0 ? `${parentNumber}.${subIdx + 1}` : parentNumber;
            }
        }
    }

    // 3. Fallback: posición secuencial en la Gantt maestra (1-indexed)
    if (tasksList && tasksList.length > 0) {
        const flat = flattenTasks(tasksList).filter(t => !t.isWeeklySubtask);
        const idx = flat.findIndex(t => t.id === task.id);
        if (idx >= 0) {
            return idx + 1;
        }
    }

    return null;
};


/**
 * Calculates the CPM slack (Late Finish - Early Finish) of all leaf tasks, anchoring the backward pass to contractualEndDate.
 */
export const calculateCriticalPathDelay = (
    tasks: Task[], 
    contractualEndDate: string,
    workingDays: boolean[] = [false, true, true, true, true, true, false], 
    holidays: { date: string; name: string }[] = []
): Map<string, number> => {
    // Helper to get local date string
    const getLocalDateStr = (date: Date): string => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    // Helper to check if a day is a working day
    const isWorkingDay = (date: Date): boolean => {
        const dayOfWeek = date.getDay();
        if (!workingDays[dayOfWeek]) return false;
        const dateStr = getLocalDateStr(date);
        return !holidays.some(h => h.date === dateStr);
    };

    const flatTasks: Task[] = [];
    const traverse = (t: Task[]) => t.forEach(task => {
        if (!task.isWeeklySubtask) {
            flatTasks.push(task);
            if (task.children) traverse(task.children);
        }
    });
    traverse(tasks);

    const slacks = new Map<string, number>();
    if (flatTasks.length === 0 || !contractualEndDate) return slacks;

    const leafTasks = flatTasks.filter(t => 
        !t.isWeeklySubtask && 
        t.type === 'task' && 
        (!t.children || t.children.length === 0 || t.children.every(c => c.isWeeklySubtask))
    );
    if (leafTasks.length === 0) return slacks;

    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    leafTasks.forEach(t => {
        if (t.start) {
            const d = new Date(t.start + "T12:00:00");
            if (!minDate || d < minDate) minDate = d;
        }
        const tEnd = t.end || t.start;
        if (tEnd) {
            const d = new Date(tEnd + "T12:00:00");
            if (!maxDate || d > maxDate) maxDate = d;
        }
    });

    const targetDate = new Date(contractualEndDate + "T12:00:00");
    if (!maxDate || targetDate > maxDate) maxDate = targetDate;
    if (!minDate) return slacks;

    const startRange = new Date(minDate);
    startRange.setDate(startRange.getDate() - 60);
    const endRange = new Date(maxDate);
    endRange.setDate(endRange.getDate() + 60);

    const workingDaysList: string[] = [];
    const dateToIdx = new Map<string, number>();

    let curr = new Date(startRange);
    let idx = 0;
    while (curr <= endRange) {
        if (isWorkingDay(curr)) {
            const dateStr = getLocalDateStr(curr);
            workingDaysList.push(dateStr);
            dateToIdx.set(dateStr, idx);
            idx++;
        }
        curr.setDate(curr.getDate() + 1);
    }

    const getWorkingDayIdx = (dateStr: string): number => {
        if (dateToIdx.has(dateStr)) return dateToIdx.get(dateStr)!;
        const targetDate = new Date(dateStr + "T12:00:00");
        let bestIdx = 0;
        let minDiff = Number.MAX_SAFE_INTEGER;
        workingDaysList.forEach((wDateStr, wIdx) => {
            const wDate = new Date(wDateStr + "T12:00:00");
            const diff = Math.abs(wDate.getTime() - targetDate.getTime());
            if (diff < minDiff) {
                minDiff = diff;
                bestIdx = wIdx;
            }
        });
        return bestIdx;
    };

    const es = new Map<string, number>();
    const ef = new Map<string, number>();
    const ls = new Map<string, number>();
    const lf = new Map<string, number>();

    leafTasks.forEach(t => {
        const tStart = t.start;
        const tEnd = t.end || t.start;
        if (tStart && tEnd) {
            es.set(t.id, getWorkingDayIdx(tStart));
            ef.set(t.id, getWorkingDayIdx(tEnd));
        }
    });

    const originalIdToTaskIdMap = new Map<string, string>();
    flatTasks.forEach(t => {
        if (t.id_original_project !== undefined && t.id_original_project !== null && !t.isWeeklySubtask) {
            originalIdToTaskIdMap.set(String(t.id_original_project), t.id);
        }
    });

    const successors = new Map<string, { id: string, type: string, lag: number }[]>();
    leafTasks.forEach(t => {
        if (t.predecessors) {
            t.predecessors.forEach(predStr => {
                const parsed = parseDependencyLink(predStr);
                if (parsed) {
                    const predOriginalId = String(parsed.rawId);
                    const predId = originalIdToTaskIdMap.get(predOriginalId);
                    if (predId) {
                        if (!successors.has(predId)) successors.set(predId, []);
                        successors.get(predId)!.push({ id: t.id, type: parsed.type, lag: parsed.lag });
                    }
                }
            });
        }
    });

    const contractualEndIdx = getWorkingDayIdx(contractualEndDate);

    leafTasks.forEach(t => {
        if (t.id && ef.has(t.id) && es.has(t.id)) {
            const taskEF = ef.get(t.id)!;
            const taskES = es.get(t.id)!;
            const duration = taskEF - taskES + 1;
            lf.set(t.id, contractualEndIdx);
            ls.set(t.id, contractualEndIdx - duration + 1);
        }
    });

    let changed = true;
    let iterations = 0;
    while(changed && iterations < 100) {
        changed = false;
        iterations++;
        leafTasks.forEach(t => {
            if (t.id && ef.has(t.id) && es.has(t.id)) {
                const succs = successors.get(t.id) || [];
                let minLF = contractualEndIdx;
                const taskEF = ef.get(t.id)!;
                const taskES = es.get(t.id)!;
                const duration = taskEF - taskES + 1;
                
                if (succs.length > 0) {
                    let calculatedMinLF = Number.MAX_SAFE_INTEGER;
                    succs.forEach(succ => {
                        const succLS = ls.get(succ.id);
                        const succLF = lf.get(succ.id);
                        const lag = succ.lag || 0;
                        if (succLS !== undefined && succLF !== undefined) {
                            let possibleLF = Number.MAX_SAFE_INTEGER;
                            if (succ.type === "FS") {
                                possibleLF = succLS - 1 - lag;
                            } else if (succ.type === "SS") {
                                possibleLF = (succLS - lag) + duration - 1;
                            } else if (succ.type === "FF") {
                                possibleLF = succLF - lag;
                            } else if (succ.type === "SF") {
                                possibleLF = (succLF - lag) + duration - 1;
                            }
                            if (possibleLF < calculatedMinLF) calculatedMinLF = possibleLF;
                        }
                    });
                    if (calculatedMinLF !== Number.MAX_SAFE_INTEGER) {
                        minLF = Math.min(minLF, calculatedMinLF);
                    }
                }
                
                const currentLF = lf.get(t.id);
                if (currentLF === undefined || minLF < currentLF) {
                    lf.set(t.id, minLF);
                    ls.set(t.id, minLF - duration + 1);
                    changed = true;
                }
            }
        });
    }

    leafTasks.forEach(t => {
        if (t.id) {
            const tEF = ef.get(t.id);
            const tLF = lf.get(t.id);
            if (tEF !== undefined && tLF !== undefined) {
                slacks.set(t.id, tLF - tEF);
            }
        }
    });

    return slacks;
};

/**
 * Calcula los días hábiles de atraso de la Ruta Crítica a una fecha de corte (cutoffDateStr),
 * evaluando el avance real acumulado de cada tarea crítica que debía ejecutarse hasta dicha fecha.
 *
 * Regla de negocio:
 * - Si las tareas de la Ruta Crítica con inicio <= cutoffDateStr llevan un avance real >= al programado a esa fecha (ej. 100%),
 *   el atraso de Ruta Crítica es 0 días.
 * - Si una tarea crítica lleva menos avance real que los días hábiles programados hasta cutoffDateStr,
 *   el desfase en días hábiles se calcula como: ceil(DíasProgramadosHastaCorte - DíasEquivalentesEjecutados).
 */
export const calculateCriticalPathDelayAtDate = (
    tasks: Task[],
    cutoffDateStr: string,
    units: PhysicalUnit[] = [],
    matrixData: Record<string, any> = {},
    protocolConfigs: Record<string, ProtocolConfig> = {},
    projectInfo: ProjectInfo = {} as ProjectInfo,
    savedProtocols: Record<string, ProtocolState> = {},
    weeklyPlans: WeeklyPlan[] = [],
    workingDays: boolean[] = [false, true, true, true, true, true, false],
    holidays: { date: string; name: string }[] = []
): number => {
    if (!tasks || tasks.length === 0 || !cutoffDateStr) return 0;

    const getLocalDateStr = (date: Date): string => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const isWorkingDay = (date: Date): boolean => {
        const dayOfWeek = date.getDay();
        if (!workingDays[dayOfWeek]) return false;
        const dStr = getLocalDateStr(date);
        return !holidays.some(h => h.date === dStr);
    };

    const countWorkingDaysBetween = (startStr: string, endStr: string): number => {
        if (!startStr || !endStr || endStr < startStr) return 0;
        let count = 0;
        const curr = new Date(startStr + "T12:00:00");
        const end = new Date(endStr + "T12:00:00");
        while (curr <= end) {
            if (isWorkingDay(curr)) count++;
            curr.setDate(curr.getDate() + 1);
        }
        return count;
    };

    const getLeafRealProgress = (task: Task): number => {
        if (task.children && task.children.length > 0) {
            const weeklySubtasks = task.children.filter(c => c.isWeeklySubtask && !c.isDeleted);
            if (weeklySubtasks.length > 0) {
                const subtaskProgresses = weeklySubtasks.map(child => {
                    const physicalProgress = calculateTaskProgress(child.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
                    const subtaskProgress = Math.min(100, (child.baseProgress !== undefined ? child.baseProgress : (child.progress || 0)) + physicalProgress);
                    const weight = child.weight !== undefined ? Number(child.weight) : (100 / weeklySubtasks.length);
                    return subtaskProgress * (weight / 100);
                });
                return Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
            }
        }
        const physicalProgress = calculateTaskProgress(task.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
        return Math.min(100, (task.baseProgress !== undefined ? task.baseProgress : (task.progress || 0)) + physicalProgress);
    };

    // Asegurar que las tareas tengan calculado isCritical mediante CPM
    const cpmTasks = calculateCriticalPath(tasks, workingDays, holidays);
    const criticalLeaves = getLeafTasksOnly(cpmTasks).filter(t => t.isCritical && t.start);

    if (criticalLeaves.length === 0) return 0;

    let maxDelayDays = 0;

    criticalLeaves.forEach(t => {
        const tStart = t.start!;
        const tEnd = t.end || t.start!;
        if (tStart > cutoffDateStr) return; // Tarea crítica futura respecto a la fecha de corte

        const realPct = getLeafRealProgress(t);
        if (realPct >= 99.99) return; // Tarea crítica al 100%, 0 días de atraso

        const totalTaskWorkingDays = Math.max(1, t.duration || countWorkingDaysBetween(tStart, tEnd) || 1);
        const plannedWorkingDaysByCutoff = cutoffDateStr >= tEnd
            ? totalTaskWorkingDays
            : Math.min(totalTaskWorkingDays, countWorkingDaysBetween(tStart, cutoffDateStr));

        const earnedWorkingDays = totalTaskWorkingDays * (realPct / 100);
        const taskDelay = Math.max(0, Math.round(plannedWorkingDaysByCutoff - earnedWorkingDays));

        if (taskDelay > maxDelayDays) {
            maxDelayDays = taskDelay;
        }
    });

    return maxDelayDays;
};

export interface MultiSCurvePoint {
    name: string;
    date: string;
    weekIndex: number;
    planActive: number | null;
    real: number | null;
    [planKey: string]: any;
}

export const calculateMultiSCurve = (
    currentTasks: Task[],
    baselines: GanttBaseline[],
    progressHistory: ProjectProgressSnapshot[] = [],
    syncDay: number = 5
): MultiSCurvePoint[] => {
    // 1. Identify the active baseline (vigente)
    const activeBaseline = baselines.find(b => b.vigente);
    const activeTasks = activeBaseline ? activeBaseline.tasksSnapshot : currentTasks;

    // 2. Define all series to calculate
    const seriesToCalculate = [
        { key: 'planActive', tasks: activeTasks }
    ];

    baselines.forEach(b => {
        if (b.selectedForChart) {
            seriesToCalculate.push({
                key: `plan_rev${b.versionNumber}`,
                tasks: b.tasksSnapshot
            });
        }
    });

    // 3. Find global date range across all series
    let globalMinStart = Infinity;
    let globalMaxEnd = -Infinity;

    const seriesLeafTasks = seriesToCalculate.map(s => {
        const leafs = getLeafTasksOnly(s.tasks);
        leafs.forEach(t => {
            if (t.start) {
                const startVal = new Date(t.start + "T12:00:00").getTime();
                if (startVal < globalMinStart) globalMinStart = startVal;
            }
            if (t.end || t.start) {
                const endVal = new Date((t.end || t.start)! + "T12:00:00").getTime();
                if (endVal > globalMaxEnd) globalMaxEnd = endVal;
            }
        });
        const totalDuration = leafs.reduce((sum, t) => sum + (t.duration || 0), 0);
        return {
            ...s,
            leafTasks: leafs,
            totalDuration
        };
    });

    // Fallback if no valid dates are found
    if (globalMinStart === Infinity) {
        globalMinStart = Date.now();
        globalMaxEnd = Date.now() + 30 * 86400000;
    }

    // 5. Generate weekly points
    const points: MultiSCurvePoint[] = [];
    let curr = new Date(globalMinStart);

    // Normalize to syncDay (default Friday)
    while (curr.getDay() !== (syncDay % 7)) {
        curr.setDate(curr.getDate() + 1);
    }

    while (curr.getTime() <= globalMaxEnd || points.length < 4) {
        const dateStr = curr.toISOString().split('T')[0];
        const dateLabel = curr.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
        const compareTime = curr.getTime();

        const point: any = {
            name: dateLabel,
            date: dateStr,
            weekIndex: points.length + 1
        };

        // Calculate planned progress for each series
        seriesLeafTasks.forEach(s => {
            let plannedDurationAccum = 0;
            s.leafTasks.forEach(t => {
                const tStart = new Date(t.start! + "T12:00:00").getTime();
                const tEnd = new Date((t.end || t.start)! + "T12:00:00").getTime();
                const tDur = t.duration || 0;

                if (compareTime >= tEnd) {
                    plannedDurationAccum += tDur;
                } else if (compareTime > tStart) {
                    const totalDays = Math.max(86400000, tEnd - tStart);
                    const elapsedDays = compareTime - tStart;
                    plannedDurationAccum += (elapsedDays / totalDays) * tDur;
                }
            });

            const planPct = s.totalDuration > 0
                ? Number(truncateProgress((plannedDurationAccum / s.totalDuration) * 100, 2))
                : 0;

            point[s.key] = Math.min(100, planPct);
        });

        // Calculate real progress from history
        let realPct: number | null = null;
        if (progressHistory && Array.isArray(progressHistory) && progressHistory.length > 0) {
            const validHistory = progressHistory.filter(h => h && h.date && h.progress !== undefined && h.progress > 0);
            if (validHistory.length > 0) {
                const sortedHistory = [...validHistory].sort((a, b) => a.date.localeCompare(b.date));
                const lastRecord = sortedHistory[sortedHistory.length - 1];
                const lastRecordTime = new Date(lastRecord.date + "T23:59:59").getTime();

                if (compareTime <= lastRecordTime) {
                    // Filter records strictly up to dateStr (exact date matching)
                    const pastOrCurrentRecords = sortedHistory.filter(r => r.date <= dateStr);

                    if (pastOrCurrentRecords.length > 0) {
                        realPct = pastOrCurrentRecords[pastOrCurrentRecords.length - 1].progress;
                    } else {
                        realPct = 0;
                    }
                }
            }
        }

        // Force initial baseline point at start of project to 0% for ALL series and real progress
        if (points.length === 0) {
            seriesLeafTasks.forEach(s => {
                point[s.key] = 0;
            });
            if (progressHistory && progressHistory.length > 0) {
                realPct = 0;
            }
        }

        point.real = realPct !== null ? Math.min(100, realPct) : null;

        points.push(point);

        curr.setDate(curr.getDate() + 7);
        if (points.length > 100) break; // Hard limit for safety
    }

    return points;
};

/**
 * Calculates the exact programmed progress (0-100) at a specific date for a list of tasks,
 * respecting the leaf-tasks only rule to avoid double counting parent tasks.
 */
export const calculateProgrammedProgressAtDate = (tasks: Task[], dateStr: string): number => {
    const leafTasks = getLeafTasksOnly(tasks);
    if (leafTasks.length === 0) return 0;

    const compareTime = new Date(dateStr + "T12:00:00").getTime();
    const totalProjectDuration = leafTasks.reduce((sum, t) => sum + (t.duration || 0), 0);

    let plannedDurationAccum = 0;
    leafTasks.forEach(t => {
        const tStart = new Date(t.start! + "T12:00:00").getTime();
        const tEnd = new Date((t.end || t.start)! + "T12:00:00").getTime();
        const tDur = t.duration || 0;

        if (compareTime >= tEnd) {
            plannedDurationAccum += tDur;
        } else if (compareTime > tStart) {
            const totalDays = Math.max(86400000, tEnd - tStart);
            const elapsedDays = compareTime - tStart;
            plannedDurationAccum += (elapsedDays / totalDays) * tDur;
        }
    });

    const planPct = totalProjectDuration > 0
        ? (plannedDurationAccum / totalProjectDuration) * 100
        : 0;

    return Math.min(100, planPct);
};

/**
 * Recursively gets all leaf tasks under a parent task node.
 */
export const getTaskLeafChildren = (task: Task): Task[] => {
    if (!task.children || task.children.length === 0) {
        return [task];
    }
    let leaves: Task[] = [];
    task.children.forEach(c => {
        leaves = leaves.concat(getTaskLeafChildren(c));
    });
    return leaves;
};

/**
 * Recursively finds a task by ID in a task tree.
 */
export const findTaskByIdInTree = (taskId: string, list: Task[]): Task | null => {
    for (const t of list) {
        if (t.id === taskId) return t;
        if (t.children && t.children.length > 0) {
            const found = findTaskByIdInTree(taskId, t.children);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Helper to update Planning EDT matrix and automatically replicate downstream to Quality EDT matrix (REGLA 3).
 */
export const syncMatrixPlanningToQuality = (
    prevMatrix: Record<string, string>,
    taskId: string,
    unitId: string,
    value: string,
    isPlanningView: boolean = true
): Record<string, string> => {
    const next = { ...prevMatrix };
    const prefix = isPlanningView ? 'planning' : 'quality';
    next[`${prefix}_${taskId}_${unitId}`] = value;

    // REGLA 3: EDT Planificación -> EDT Calidad (Downstream Sync)
    if (isPlanningView) {
        next[`quality_${taskId}_${unitId}`] = value;
    }
    return next;
};

export interface ParsedDependencyLink {
    rawId: string;
    id: number | string;
    type: 'FS' | 'SS' | 'FF' | 'SF';
    lag: number;
    lagSuffix: string;
    normalized: string;
}

/**
 * Parsea y normaliza un enlace de dependencia de Carta Gantt con soporte de desfase (+X / -X días).
 * Ejemplos soportados: "12", "12FC", "12FS+2", "12FC - 1", "12CC+3d", "12+2", "12-1días"
 */
export const parseDependencyLink = (str: string): ParsedDependencyLink | null => {
    if (!str || typeof str !== 'string') return null;
    const match = str.trim().match(/^([\d.]+)\s*(FS|FC|SS|CC|FF|SF|CF)?\s*([+-]\s*\d+)?\s*(?:d|d[ií]as?)?$/i);
    if (!match) return null;

    const rawId = match[1];
    const id = /^\d+$/.test(rawId) ? parseInt(rawId, 10) : rawId;
    let rawType = (match[2] || 'FS').toUpperCase();
    if (rawType === 'FC') rawType = 'FS';
    if (rawType === 'CC') rawType = 'SS';
    if (rawType === 'CF') rawType = 'SF';
    const type = rawType as 'FS' | 'SS' | 'FF' | 'SF';

    let lag = 0;
    if (match[3]) {
        lag = parseInt(match[3].replace(/\s+/g, ''), 10) || 0;
    }
    const lagSuffix = lag > 0 ? `+${lag}` : lag < 0 ? `${lag}` : '';
    const normalized = `${rawId}${type}${lagSuffix}`;

    return { rawId, id, type, lag, lagSuffix, normalized };
};

/**
 * Calculates Critical Path Delay in days at a given cutoff date based on CPM negative slacks.
 */
export const getCriticalPathDelayAtDate = (
    tasks: Task[],
    dateStr: string,
    contractualEndDate?: string,
    workingDays: boolean[] = [false, true, true, true, true, true, false],
    holidays: { date: string; name: string }[] = []
): number => {
    if (!tasks || tasks.length === 0) return 0;
    const endDate = contractualEndDate || (tasks[0]?.end || "");
    if (!endDate) return 0;

    const slacks = calculateCriticalPathDelay(tasks, endDate, workingDays, holidays);
    let maxDelay = 0;
    slacks.forEach((slack, taskId) => {
        if (slack < 0) {
            const t = findTaskById(taskId, tasks);
            if (t && t.start && t.start <= dateStr) {
                const delayDays = Math.abs(slack);
                if (delayDays > maxDelay) {
                    maxDelay = delayDays;
                }
            }
        }
    });
    return maxDelay;
};
